/**
 * calendarHoursUtils.js
 * Converts raw API calendar event data into the shapes FullCalendar expects
 * for businessHours and background (closure/holiday) events.
 */

const DAY_MAP = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 0,
};

const toMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(":").map(Number);
  return (Number.isNaN(h) ? 0 : h) * 60 + (Number.isNaN(m) ? 0 : m);
};

/**
 * Converts WEEKLY OPEN API events into the FullCalendar businessHours array
 * used on each resource.
 */
export const convertEventsToRegularHours = (events) => {
  const regularHours = [];

  events
    .filter(
      (evt) =>
        evt.event_recurring_type === "WEEKLY" && evt.event_statue_type === "OPEN"
    )
    .forEach((evt) => {
      const dayOfWeek = DAY_MAP[evt.dayOfWeek];
      if (dayOfWeek === undefined) return;

      let startTime = evt.startTime?.slice(0, 5) || "00:00";
      let endTime = evt.endTime?.slice(0, 5) || "24:00";

      // Equal start/end means the dock is open all day
      if (startTime === endTime) {
        startTime = "00:00";
        endTime = "24:00";
      }

      const startMinutes = toMinutes(startTime);
      const endMinutes = toMinutes(endTime);

      // Cross-midnight shift: split into two segments
      if (startMinutes > endMinutes && endTime !== "24:00") {
        regularHours.push({ daysOfWeek: [dayOfWeek], startTime, endTime: "24:00" });
      } else {
        regularHours.push({ daysOfWeek: [dayOfWeek], startTime, endTime });
      }
    });

  return regularHours;
};

/**
 * Converts YEARLY CLOSE API events into FullCalendar background events (holidays).
 */
export const convertHolidaysToExceptionEvents = (events, dockId) =>
  events
    .filter(
      (evt) =>
        evt.event_recurring_type === "YEARLY" && evt.event_statue_type === "CLOSE"
    )
    .map((evt) => ({
      id: `holiday-${dockId}-${evt.eventId}`,
      title: evt.eventDescription || "Holiday",
      start: evt.startDate,
      end: evt.endDate || evt.startDate,
      display: "background",
      resourceId: String(dockId),
      overlap: false,
      editable: false,
      classNames: ["exception-event"],
    }));

/**
 * Converts recurring background events (holidays + blackout days) into
 * FullCalendar background events. No edit/delete buttons — no exceptionId.
 */
export const convertRecurringClosuresToExceptionEvents = (events, dockId) =>
  events
    .filter(
      (evt) =>
        evt.event_statue_type === "CLOSE" &&
        evt.event_recurring_type === "YEARLY" &&
        evt.dayOfMonth != null &&
        evt.monthOfYear != null
    )
    .map((evt) => {
      // Build the actual recurring date for the current year
      const year = new Date().getFullYear();
      const month = String(evt.monthOfYear).padStart(2, "0");
      const day = String(evt.dayOfMonth).padStart(2, "0");
      const dateStr = `${year}-${month}-${day}`;

      // +1 day for exclusive end
      const endDate = new Date(`${dateStr}T00:00:00`);
      endDate.setDate(endDate.getDate() + 1);
      const endDateStr = endDate.toISOString().split("T")[0];

      return {
        id: `holiday-${dockId}-${evt.eventId}`,
        title: evt.eventDescription || "Holiday",
        start: dateStr,
        end: endDateStr,
        display: "background",
        resourceId: String(dockId),
        overlap: false,
        editable: false,
        classNames: ["exception-event"],
        extendedProps: {
          reason: evt.eventDescription || "Holiday",
          rendering: "background",
          isWholeDay: true,
        },
      };
    });

/**
 * Converts Blackout Exception events into FullCalendar background events
 */
export const convertBlackoutExceptionToFCEvent = (evt, dockKey) => {
  const endDate = new Date(`${evt.endDate || evt.startDate}T00:00:00`);
  endDate.setDate(endDate.getDate() + 1);
  const endDateStr = endDate.toISOString().split("T")[0];

  return {
    id: `blackout-${dockKey}-${evt.exceptionId}`,
    title: evt.reason || "Blackout",
    start: evt.startDate,
    end: endDateStr,
    display: "background",
    resourceId: String(dockKey),
    overlap: false,
    editable: false,
    classNames: ["exception-event"],
    extendedProps: {
      reason: evt.reason || "Blackout",
      rendering: "background",
      isWholeDay: true,
    },
  };
};

/**
 * Converts a one-time exception event (closure) into a FullCalendar background event.
 * Extracted from the inline .map() in fetchDocksForLocation for reuse and testability.
 */

  export const convertExceptionEventToFCEvent = (evt, dockKey) => {
    const isWholeDay = !evt.startTime?.trim() && !evt.endTime?.trim();

    const exceptionKey =
      evt.exceptionId ||
      evt.exceptionEventId ||
      `${evt.startDate}-${evt.startTime || "allday"}-${evt.endDate}-${evt.endTime || "allday"}`;

    const reason = evt.reason || "Closed";

    return {
      id: `exception-${dockKey}-${exceptionKey}`,
      title: reason,
      start: isWholeDay ? `${evt.startDate}T00:00:00`                        : `${evt.startDate}T${evt.startTime}`,
      end:   isWholeDay ? `${evt.endDate || evt.startDate}T23:59:59`         : `${evt.endDate}T${evt.endTime}`,
      display: "background",
      resourceId: dockKey,
      overlap: false,
      editable: false,
      classNames: ["exception-event"],
      extendedProps: {
        exceptionId: evt.exceptionId || evt.exceptionEventId,
        reason,
        rendering: "background",
      },
    };

};